import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Search as SearchIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material'
import CardTitle from 'src/components/sharedComponents/CardTitle'
import { getRolUser, getEscenariosCompra } from '@auteco/services/motosSnopServices'
import ResumenCompra from './ResumenCompra'
import DetalleCompra from './DetalleCompra'
import ResumenCompraDetalle from './ResumenCompraDetalle'

const CompraContainer = () => {
  const [tabValue, setTabValue] = useState(0)
  const [infoBars, setInfoBars] = useState({})
  const [detalleParams, setDetalleParams] = useState({})
  const [resumenDetalleParams, setResumenDetalleParams] = useState({})
  const [escenarioSeleccionado, setEscenarioSeleccionado] = useState('inicial_2025_6')
  const [tipoNegocio, setTipoNegocio] = useState('TVS')
  const [escenarios, setEscenarios] = useState([])

  const getInfoBars = async () => {
    const response = await getRolUser()
    if (response) {
      setInfoBars(response.iam.nombre)
    }
  }

  useEffect(() => {
    getInfoBars()
  }, [])

  // Cargar escenarios a nivel del contenedor para que la información de
  // "solo lectura" (es_actualizado) esté disponible en todas las pestañas.
  const fetchEscenarios = useCallback(async () => {
    try {
      const data = await getEscenariosCompra(tipoNegocio)
      if (data && data.escenarios) {
        setEscenarios(data.escenarios)
      }
    } catch (error) {
      console.error('Error fetching escenarios:', error)
    }
  }, [tipoNegocio])

  useEffect(() => {
    fetchEscenarios()
  }, [fetchEscenarios])

  // Un escenario "actualizado" (es_actualizado === 1) es de solo lectura:
  // se puede consultar y filtrar, pero no editar en ninguna pestaña.
  const esSoloLectura = useMemo(() => {
    const actual = escenarios.find((e) => e.escenario === escenarioSeleccionado)
    return Boolean(actual && Number(actual.es_actualizado) === 1)
  }, [escenarios, escenarioSeleccionado])

  const handleTabChange = (event, newValue) => {
    // Si volvemos a Resumen, limpiar los parámetros de detalle
    // Esto fuerza que Detalle recargue desde localStorage la próxima vez
    if (newValue === 0) {
      setDetalleParams({})
      setResumenDetalleParams({})
    }
    setTabValue(newValue)
  }

  const handleNavigateToDetalle = (tipoNegocioParam, modelo, modelosFiltrados) => {
    // Actualizar el tipo de negocio si es diferente al actual
    if (tipoNegocioParam && tipoNegocioParam !== tipoNegocio) {
      setTipoNegocio(tipoNegocioParam)
    }
    setDetalleParams({ compania: tipoNegocioParam, modelo, modelosFiltrados })
    setTabValue(2) // Cambiar al tab de detalle
  }

  const handleNavigateToResumenDetalle = (tipoNegocioParam, modelo, modelosFiltrados) => {
    // Actualizar el tipo de negocio si es diferente al actual
    if (tipoNegocioParam && tipoNegocioParam !== tipoNegocio) {
      setTipoNegocio(tipoNegocioParam)
    }
    setResumenDetalleParams({ compania: tipoNegocioParam, modelo, modelosFiltrados })
    setTabValue(1) // Cambiar al tab de resumen detalle
  }

  const TabPanel = ({ children, value, index, ...other }) => (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`compra-tabpanel-${index}`}
        aria-labelledby={`compra-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 0.5 }}>{children}</Box>}
      </div>
    )

  const a11yProps = (index) => ({
      id: `compra-tab-${index}`,
      'aria-controls': `compra-tabpanel-${index}`,
    })

  if (infoBars !== 'Comprador') {
    return (
      <div>
        <p>No tienes permisos para acceder a esta página</p>
      </div>
    )
  }
  return (
    <Box sx={{ px: 1, py: 0.75 }}>
      <CardTitle title="Compras" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Compras tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 120,
              fontWeight: 600,
              color: '#666',
              py: 0.5,
              minHeight: 40,
              '&.Mui-selected': {
                color: '#4c75aa',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#4c75aa',
              height: 3,
            },
          }}
        >
          <Tab
            icon={<SearchIcon />}
            iconPosition="start"
            label="Resumen"
            {...a11yProps(0)}
            sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
            }}
          />
          <Tab
            icon={<ExportIcon />}
            iconPosition="start"
            label="Resumen flujo"
            {...a11yProps(1)}
            sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
            }}
          />
          <Tab
            icon={<ExportIcon />}
            iconPosition="start"
            label="Detalle"
            {...a11yProps(2)}
            sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
            }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <ResumenCompra
          onNavigateToDetalle={handleNavigateToDetalle}
          onNavigateToResumenDetalle={handleNavigateToResumenDetalle}
          escenarioSeleccionado={escenarioSeleccionado}
          setEscenarioSeleccionado={setEscenarioSeleccionado}
          tipoNegocio={tipoNegocio}
          setTipoNegocio={setTipoNegocio}
          escenarios={escenarios}
          refetchEscenarios={fetchEscenarios}
          esSoloLectura={esSoloLectura}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ResumenCompraDetalle 
          initialParams={resumenDetalleParams}
          escenarioSeleccionado={escenarioSeleccionado}
          tipoNegocio={tipoNegocio}
          setTipoNegocio={setTipoNegocio}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <DetalleCompra
          initialParams={detalleParams}
          escenarioSeleccionado={escenarioSeleccionado}
          tipoNegocio={tipoNegocio}
          esSoloLectura={esSoloLectura}
        />
      </TabPanel>
    </Box>
  )
}

export default CompraContainer

